import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
} from "@mui/material";

const listLabel = (folder) =>
  folder.distributionName_chr || folder.generatedDistributionName_chr ||
  folder.distributionListName_chr || folder.distributionListName || "Distribution list";

const distributionListKey = (folder) =>
  folder.distributionListID_ids ?? folder.distributionListID ?? folder.distribution_list_id;

const submissionFolderKey = (folder) =>
  folder.submissionFolderID_ids ?? folder.submissionFolderID ?? folder.submission_folder_id ?? folder.id;

// MUI's default InputLabel position isn't vertically centered in an unshrunk Select.
const centeredLabelSx = {
  "& .MuiInputLabel-root": {
    top: "50%",
    left: 14,
    transform: "translateY(-50%)",
  },
  "& .MuiInputLabel-shrink": {
    top: 0,
    left: 14,
    transform: "translateY(-50%) scale(0.75)",
  },
};

export default function SubmitCollectionDialog({ open, folders, onClose, onSubmit, saving = false }) {
  const [distributionListId, setDistributionListId] = useState("");
  const [folderId, setFolderId] = useState("");

  const distributionLists = useMemo(() => {
    const map = new Map();
    folders.forEach((folder) => {
      const listId = distributionListKey(folder);
      if (listId != null && !map.has(String(listId))) {
        map.set(String(listId), {
          id: listId,
          label: listLabel(folder),
        });
      }
    });
    return [...map.values()];
  }, [folders]);

  const availableFolders = useMemo(
    () => folders.filter((folder) => String(distributionListKey(folder)) === String(distributionListId)),
    [distributionListId, folders]
  );
  const selectedFolder = availableFolders.find((folder) => String(submissionFolderKey(folder)) === String(folderId));

  useEffect(() => {
    if (!open) return;
    const onlyList = distributionLists.length === 1 ? String(distributionLists[0].id) : "";
    setDistributionListId(onlyList);
    setFolderId("");
  }, [distributionLists, open]);

  const submit = async () => {
    if (!folderId) return;
    await onSubmit(Number(folderId));
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>Submit Collection</DialogTitle>
      <DialogContent>
        <Stack spacing={2} mt={1}>
          <FormControl fullWidth sx={centeredLabelSx}>
            <InputLabel>Distribution List</InputLabel>
            <Select
              value={distributionListId}
              label="Distribution List"
              onChange={(event) => {
                setDistributionListId(event.target.value);
                setFolderId("");
              }}
            >
              {distributionLists.map((list) => <MenuItem key={list.id} value={list.id}>{list.label}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl fullWidth disabled={!distributionListId} sx={centeredLabelSx}>
            <InputLabel>Group Folder</InputLabel>
            <Select value={folderId} label="Group Folder" onChange={(event) => setFolderId(event.target.value)}>
              {availableFolders.map((folder) => (
                <MenuItem key={submissionFolderKey(folder)} value={submissionFolderKey(folder)}>
                  {folder.folderName_chr || folder.folderName || folder.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {(selectedFolder?.criteria_chr || selectedFolder?.criteria || selectedFolder?.criteria_note) && (
            <Alert severity="info">
              {selectedFolder.criteria_chr || selectedFolder.criteria || selectedFolder.criteria_note}
            </Alert>
          )}
          {distributionLists.length === 0 && (
            <Alert severity="info">You are not currently eligible to submit to a group folder.</Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button onClick={submit} variant="contained" disabled={!folderId || saving}>
          {saving ? "Submitting…" : "Submit"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
